import { useState } from "react";
import { Paperclip, Link2, AlertTriangle, Download, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const fmtSize = (b) => {
  if (!b) return "–";
  if (b < 1e6) return `${Math.max(1, Math.round(b / 1e3))} KB`;
  return `${(b / 1e6).toFixed(1).replace(".", ",")} MB`;
};
const fmtDate = (s) => new Date(s).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

function Line({ label, value, testid }) {
  if (!value) return null;
  return (
    <div className="flex gap-2" data-testid={testid}>
      <span className="w-14 shrink-0 text-zinc-500">{label}</span>
      <span className="text-zinc-200 break-all">{value}</span>
    </div>
  );
}

function OpenStatus({ proof, id }) {
  if (!proof.ok || !proof.tracking) return null;
  const n = proof.open_count || 0;
  if (!proof.first_opened_at) {
    return (
      <div className="flex items-center gap-1.5 text-zinc-400" data-testid={`send-proof-open-${id}`}>
        <EyeOff size={13} /> Noch nicht geöffnet
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5 text-[#00E575] font-semibold" data-testid={`send-proof-open-${id}`}>
      <Eye size={13} /> Vom Kunden geöffnet am {fmtDate(proof.first_opened_at)}
      {n > 1 && <span className="text-zinc-400 font-normal">· {n}× · zuletzt {fmtDate(proof.last_opened_at)}</span>}
    </div>
  );
}

function FileRow({ f, i, id }) {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/history/${id}/attachments/${i}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: f.name });
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      toast.error("Download fehlgeschlagen");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-1.5 text-zinc-200" data-testid={`send-proof-file-${id}-${i}`}>
      <Paperclip size={12} className="text-[#00E575] shrink-0" />
      <span className="truncate">{f.name}</span>
      <span className="text-zinc-500 font-mono shrink-0">· {fmtSize(f.size_bytes)}</span>
      {f.storage_path && (
        <button type="button" onClick={download} disabled={busy} title="Genau diese Datei herunterladen"
                data-testid={`send-proof-download-${id}-${i}`}
                className="ml-auto h-7 px-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[11.5px] font-semibold inline-flex items-center gap-1 shrink-0 disabled:opacity-50">
          {busy ? <Loader2 size={11} className="animate-spin" /> : <Download size={11} />} Laden
        </button>
      )}
    </div>
  );
}

// Versand-Nachweis unter einem Chronik-Eintrag: Absender, Empfänger, Anhänge, geöffnet.
export function SendProof({ proof, id }) {
  const files = proof.attachments || [];
  const links = proof.links || [];
  return (
    <div className="mt-2 rounded-xl border border-zinc-800 bg-[#0E0E12] p-3 text-[12px] space-y-1" data-testid={`send-proof-${id}`}>
      {!proof.ok && (
        <div className="flex items-start gap-1.5 text-rose-300 font-semibold mb-1" data-testid={`send-proof-error-${id}`}>
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> Nicht zugestellt{proof.error ? `: ${proof.error}` : ""}
        </div>
      )}
      <OpenStatus proof={proof} id={id} />
      {proof.fallback && (
        <div className="flex items-start gap-1.5 text-amber-300" data-testid={`send-proof-fallback-${id}`}>
          <AlertTriangle size={13} className="mt-0.5 shrink-0" /> {proof.fallback}
        </div>
      )}
      <Line label="Durch" value={proof.sent_by_name} testid={`send-proof-by-${id}`} />
      <Line label="Von" value={proof.from} testid={`send-proof-from-${id}`} />
      <Line label="An" value={(proof.to || []).join(", ")} testid={`send-proof-to-${id}`} />
      <Line label="CC" value={(proof.cc || []).join(", ")} testid={`send-proof-cc-${id}`} />
      <Line label="BCC" value={(proof.bcc || []).join(", ")} testid={`send-proof-bcc-${id}`} />
      <Line label="Antwort" value={proof.reply_to} testid={`send-proof-replyto-${id}`} />
      <div className="pt-1.5 mt-1 border-t border-zinc-800/80 space-y-1">
        <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-bold">Anhänge ({files.length})</div>
        {files.length === 0 && <div className="text-zinc-500">Keine Anhänge</div>}
        {files.map((f, i) => <FileRow key={i} f={f} i={i} id={id} />)}
        {links.map((f, i) => (
          <div key={`l${i}`} className="flex items-center gap-1.5 text-sky-300" data-testid={`send-proof-link-${id}-${i}`}>
            <Link2 size={12} className="shrink-0" />
            <span className="truncate">{f.name}</span>
            <span className="text-zinc-500 font-mono shrink-0">· {fmtSize(f.size_bytes)} · als Download-Link</span>
          </div>
        ))}
      </div>
    </div>
  );
}
